import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Ticket, ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';
import { useClipboardCode } from '@/lib/useClipboardCode';
import { resolvePoolCode } from '@/lib/resolvePoolCode';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { Section, SectionTitle, MediaCards, EmojiCards, ScreenShot, MetricRow, FaqList, CtaBand, TextLink } from '@/components/marketing/sections';
import { FONT_B, FONT_H, GRADIENT_BG, GRADIENT_HIGHLIGHT, IMG, SIGNUP_PATH, fadeUp, stagger } from '@/components/marketing/theme';

/* ─── Typewriter ─── */
const TYPEWRITER_WORDS = ['friends', 'groups', 'clubs', 'bars', 'fundraising'];
const TYPING_SPEED = 60;
const DELETING_SPEED = 35;
const PAUSE_AFTER_TYPE = 1400;
const PAUSE_AFTER_DELETE = 250;

function useTypewriter() {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = TYPEWRITER_WORDS[wordIdx];
    let timeout;
    if (!deleting && charIdx <= word.length) {
      if (charIdx === word.length) {
        timeout = setTimeout(() => setDeleting(true), PAUSE_AFTER_TYPE);
      } else {
        timeout = setTimeout(() => { setDisplay(word.slice(0, charIdx + 1)); setCharIdx(charIdx + 1); }, TYPING_SPEED);
      }
    } else if (deleting && charIdx >= 0) {
      if (charIdx === 0) {
        timeout = setTimeout(() => { setDeleting(false); setWordIdx((wordIdx + 1) % TYPEWRITER_WORDS.length); }, PAUSE_AFTER_DELETE);
      } else {
        timeout = setTimeout(() => { setDisplay(word.slice(0, charIdx - 1)); setCharIdx(charIdx - 1); }, DELETING_SPEED);
      }
    }
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx]);

  return display;
}

/* ─── HERO (unchanged by request) ─── */
function Hero() {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [joining, setJoining] = useState(false);
  const typedWord = useTypewriter();
  const { paste, supported: clipboardSupported, pasting } = useClipboardCode((code) => setAccessCode(code));

  const handleJoin = async (e) => {
    e.preventDefault();
    const code = accessCode.trim().toUpperCase();
    if (!code) return;
    setJoining(true);
    try {
      // Probe every sport — golf, football (LMS) and racing all mint codes
      // from the same namespace, so checking golf alone rejected valid ones.
      const resolved = await resolvePoolCode(code);
      if (!resolved) {
        toast.error('Invalid access code. Please check and try again.');
        return;
      }
      navigate(resolved.gatekeeperPath);
    } catch {
      toast.error('Couldn\u2019t check that code right now. Please try again in a moment.');
    } finally { setJoining(false); }
  };

  return (
    <section className="pt-24 pb-10 md:pt-32 md:pb-24 relative overflow-hidden" data-testid="landing-hero">
      <div className="absolute top-0 left-0 right-0 pointer-events-none" aria-hidden>
        <picture>
          <source media="(max-width: 767px)" type="image/webp" srcSet="/hero/hero-bg-mobile.webp" />
          <source media="(max-width: 767px)" srcSet="/hero/hero-bg-mobile.png" />
          <source type="image/webp" srcSet="/hero/hero-bg-desktop.webp" />
          <img src="/hero/hero-bg-desktop.png" alt="" fetchPriority="high" decoding="async" className="w-full h-auto opacity-[0.06]" />
        </picture>
      </div>
      <div className="absolute left-0 right-0 bottom-0 h-24 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,1) 100%)' }} aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto text-center">
          <motion.h1 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.15] mb-3 text-gray-900" style={FONT_H}>
            <span className="block">Run sports pools</span>
            <span className="block pb-1.5" style={GRADIENT_HIGHLIGHT}>without the spreadsheet nightmare</span>
          </motion.h1>
          <motion.div variants={fadeUp} className="mb-6 md:mb-10 mt-1" data-testid="hero-typewriter">
            <span className="text-base sm:text-xl font-bold text-gray-800" style={FONT_H}>Perfect for </span>
            <span className="text-base sm:text-xl font-bold" style={{ ...FONT_H, ...GRADIENT_HIGHLIGHT }}>{typedWord}</span>
            <span className="inline-block w-[2px] h-[1.2em] align-middle ml-0.5 animate-pulse" style={{ background: GRADIENT_BG }} />
          </motion.div>
          <motion.div variants={fadeUp} className="mb-7 md:mb-10">
            <Link
              to={SIGNUP_PATH}
              className="w-full sm:w-auto rounded-full font-bold px-10 py-4 text-base text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
              style={{ ...FONT_B, background: GRADIENT_BG }}
              data-testid="hero-start-pool"
            >
              Start a Pool <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div variants={fadeUp} className="max-w-md mx-auto">
            <p className="text-[10px] sm:text-xs text-gray-400 mb-2 font-semibold tracking-[0.08em]" style={FONT_B}>ALREADY GOT AN ACCESS CODE?</p>
            <form onSubmit={handleJoin} className="group flex items-center gap-2 bg-white/80 backdrop-blur rounded-full border border-gray-200 shadow-sm px-3 py-1.5 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-500/10 transition-all" data-testid="hero-access-code-form">
              <Ticket className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" />
              <input
                type="text"
                value={accessCode}
                onChange={e => setAccessCode(e.target.value.toUpperCase())}
                placeholder="Enter 6-digit code"
                maxLength={10}
                autoComplete="one-time-code"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                className="flex-1 min-w-0 bg-transparent text-sm font-semibold tracking-widest uppercase outline-none py-2 placeholder:tracking-normal placeholder:font-normal placeholder:normal-case placeholder:text-gray-400"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                data-testid="hero-access-code-input"
              />
              <button
                type="submit"
                disabled={joining}
                className="rounded-full font-bold px-4 py-1.5 text-white text-xs flex-shrink-0 shadow-sm opacity-40 group-focus-within:opacity-100 hover:opacity-100 disabled:cursor-wait transition-opacity duration-200"
                style={{ ...FONT_B, background: GRADIENT_BG }}
                data-testid="hero-access-code-submit"
              >
                {joining ? '...' : 'Join'}
              </button>
            </form>
            {clipboardSupported && !accessCode && (
              <button type="button" onClick={paste} disabled={pasting} className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50" style={FONT_B} data-testid="hero-paste-code-btn">
                <ClipboardPaste className="w-3.5 h-3.5" />
                {pasting ? 'Pasting…' : 'Paste code from clipboard'}
              </button>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── CONTENT (matches the design file) ─── */
const SPORT_CARDS = [
  { title: 'Golf', image: IMG.golfSociety, alt: 'Golf pools', desc: 'Points & Tier Pick pools · every PGA Tournament' },
  { title: 'Football', image: IMG.football, alt: 'Football pools', desc: 'Last Man Standing · all season' },
  { title: 'Racing', image: IMG.racing, alt: 'Racing sweepstakes', desc: 'Sweepstakes · Cheltenham & the big days' },
];

const AUDIENCES = [
  { title: 'Golf societies', emoji: '⛳', to: '/golf-societies', desc: 'Every PGA Tournament, live scoring, season leaderboard.' },
  { title: 'Sports clubs', emoji: '🏉', to: '/sports-clubs', desc: 'Member socials and fundraisers for GAA, rugby and more.' },
  { title: 'Bars & pubs', emoji: '🍺', to: '/bars-and-pubs', desc: 'QR posters and the TV leaderboard on the big screen.' },
  { title: 'Workplaces', emoji: '💼', to: '/workplaces', desc: 'Office sweepstakes without the email chains.' },
  { title: 'Friend groups', emoji: '👥', to: '/friend-groups', desc: 'The WhatsApp group, minus the spreadsheet.' },
];

const METRICS = [
  { metric: 'Live', label: 'Auto-sync 60s' },
  { metric: '10+', label: 'TV templates' },
  { metric: '2 min', label: 'Pool setup' },
];

const FAQS = [
  { q: 'Who can use SportSweeps?', a: 'Anyone. Golf groups, friend groups, clubs, pubs, bars, work teams. No minimum size required.' },
  { q: 'How does live scoring work?', a: 'Scores sync automatically from live data every 60 seconds.' },
  { q: 'Do players need an account?', a: 'Only to submit picks - browsing needs just the access code.' },
];

export default function LandingPage() {
  return (
    <MarketingLayout testId="landing-page">
      <Hero />

      <Section testId="landing-sports">
        <SectionTitle eyebrow="One platform, every sweep" className="mb-5 md:mb-9">Golf, football and racing pools</SectionTitle>
        <MediaCards items={SPORT_CARDS} testId="landing-sport-cards" />
      </Section>

      <Section tone="lilac" testId="landing-who-its-for">
        <SectionTitle eyebrow="Who it's for" className="mb-5 md:mb-9">Built for your group</SectionTitle>
        <EmojiCards items={AUDIENCES} testId="landing-audiences" />
      </Section>

      <Section testId="landing-showcase">
        <SectionTitle eyebrow="See it in action" className="mb-5 md:mb-8">A dashboard your players will love</SectionTitle>
        <ScreenShot src="/screens/admin-dashboard.png" alt="SportSweeps admin dashboard" testId="landing-dashboard-shot" />
        <div className="max-w-[860px] mx-auto mt-5 md:mt-8">
          <MetricRow items={METRICS} testId="landing-metrics" />
        </div>
      </Section>

      <Section tone="lilac" testId="landing-faq">
        <SectionTitle className="mb-3 md:mb-6">Got questions?</SectionTitle>
        <FaqList items={FAQS} testId="landing-faq-list" />
        <p className="text-center mt-4" data-testid="landing-faq-more">
          <TextLink to="/faq" testId="landing-faq-see-all">See all questions &rarr;</TextLink>
        </p>
      </Section>

      <CtaBand
        tone="white"
        title="Ready to ditch the spreadsheet?"
        lead="Set up your first pool in under 2 minutes."
        ctaLabel="Start Your Group"
      />
    </MarketingLayout>
  );
}
